/** Pure logic for the tips page (one meeting, results folded in). No React here. */

// ---------- data shapes (the subset of tipsPage / getEnhancedRaceResults the page reads) ----------

export interface DetailedInfo {
  horse_age?: number | null;
  horse_sex?: string | null;
  horse_sire?: string | null;
  horse_dam?: string | null;
  horse_last10?: string | null;
  horse_record?: string | null;
  horse_record_distance?: string | null;
  horse_record_track?: string | null;
  horse_record_track_distance?: string | null;
  horse_record_firm?: string | null;
  horse_record_good?: string | null;
  horse_record_soft?: string | null;
  horse_record_heavy?: string | null;
  horse_record_synthetic?: string | null;
  horse_record_first_up?: string | null;
  horse_record_second_up?: string | null;
  horse_prize_money?: string | null;
}

export interface Selection {
  number: number;
  horse_name: string;
  jockey?: string | null;
  trainer?: string | null;
  weight?: number | null;
  barrier?: number | null;
  odds: number;
  assessedOdds: number;
  adjustedAssessedProb?: number | null;
  oddsOpening?: number | null;
  oddsCurrent?: number | null;
  oddsUpdatedAt?: string | null;
  pick_order?: number | null;
  confidence: number;
  isConsensusPick?: boolean | null;
  /**
   * Staff-only trial (null for customers): a metro or provincial #1 top pick whose write-up
   * says it has run to a winning standard. Shown with a gold ring round its
   * number (a gold border on high-probability rows).
   */
  isStandardClaim?: boolean | null;
  /** Pick 1 only: how many of the race's model slots made this horse their #1, out of topPickSlots. */
  topPickAgreement?: number | null;
  topPickSlots?: number | null;
  analysis?: { summary?: string | null; pros?: string | null; cons?: string | null } | null;
  bet_type?: string | null;
  bet_stake?: string | null;
  detailedInfo?: DetailedInfo | null;
}

export interface SpeedMapRunner {
  tabNumber: number;
  barrier: number | null;
  horseName: string;
  bucket: string;
  mapPosition?: number | null;
  settleAvg: number | null;
}

export interface SpeedMap {
  likelyTempo?: string | null;
  tempoRationale?: string | null;
  runners: SpeedMapRunner[];
}

export interface Race {
  race_id: string;
  race_number: number;
  race_name: string;
  distance: number;
  class?: string | null;
  start_time?: string | null;
  group?: string | null;
  /** The AI model that wrote the race's tips; the API sends it to staff only. */
  aiModel?: string | null;
  selections?: { top?: Selection[] | null; value?: Selection[] | null; speedMap?: SpeedMap | null } | null;
}

export interface Scratching {
  raceNo: number;
  tabNo?: number | null;
  runnerName?: string;
}

export interface ResultRow {
  runner_id?: string | null;
  position?: number | null;
  position_finish?: number | null;
  horse_name: string;
  price_sp?: number | null;
  price_place?: number | null;
  scratched?: boolean | null;
  pick_order?: number | null;
  pick_type?: string | null;
}

export interface RaceResults {
  race_number: number;
  results?: ResultRow[] | null;
}

/** One race's markers from meetings.raceResultSegments. */
export interface Segment {
  race_number: number;
  quinella_hit?: boolean | null;
  trifecta_hit?: boolean | null;
  first_four_hit?: boolean | null;
}

// ---------- small helpers ----------

/** The data doubles apostrophes ("Grey Ol'' Day"); the old pages undid it. */
export const clean = (s: string | null | undefined): string => (s ?? '').replace(/''/g, "'");

/** Pros/cons arrive as one "|"-separated string, sometimes wrapped in quotes. */
export const splitPoints = (s: string | null | undefined): string[] => {
  let t = (s ?? '').trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) t = t.slice(1, -1);
  return clean(t)
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean);
};

export type BetClass = 'win' | 'place' | 'eachway' | 'nobet';

/** Bet type → card colour. Reads bet_type, then the stake text ("2 units each-way"). */
export const betClass = (betType: string | null | undefined, stake?: string | null): BetClass => {
  const t = `${betType ?? ''} ${stake ?? ''}`.toLowerCase();
  if (t.includes('no bet')) return 'nobet';
  if (t.includes('each') || t.includes('e/w')) return 'eachway';
  if (t.includes('place')) return 'place';
  if (t.includes('win')) return 'win';
  return 'nobet';
};

/** Our chance of winning, 0-100: the adjusted figure, else what our price implies. */
export const chanceOf = (s: Selection): number | null =>
  s.adjustedAssessedProb != null ? s.adjustedAssessedProb : s.assessedOdds > 0 ? 100 / s.assessedOdds : null;

/** Finishing position of a result row, or 0 when it has not run. */
export const positionOf = (r: ResultRow | null | undefined): number => Number(r?.position || r?.position_finish || 0);

const finished = (pos: number) => pos > 0 && pos !== 999;

/** A race has run once any runner has a finishing position (as TipsPage decided). */
export const isRun = (race: RaceResults | null | undefined): boolean =>
  !!race?.results?.some((r) => positionOf(r) > 0);

export const isScratched = (scratchings: Scratching[] | null | undefined, raceNo: number, tabNo: number): boolean =>
  !!scratchings?.some((s) => s.raceNo === raceNo && s.tabNo === tabNo);

/** A horse's result: by tab number (last part of runner_id), else by name. Null before the race is run. */
export function findResult(race: RaceResults | null | undefined, number: number, name: string): ResultRow | null {
  if (!race?.results || !isRun(race)) return null;
  const byNumber = race.results.find((r) => {
    const n = parseInt((r.runner_id ?? '').split('-').pop() ?? '', 10);
    return n === number;
  });
  return byNumber ?? race.results.find((r) => r.horse_name?.toLowerCase() === name.toLowerCase()) ?? null;
}

// ---------- odds movement (ported from utils/oddsMovement, banded by win-probability shift) ----------

export interface OddsMove {
  /** Latest collected price, else the analysis price. */
  odds: number;
  /** "▼ in from $7.50" / "▲ out from $4.00", or undefined when steady. */
  label?: string;
  dir?: 'in' | 'out';
  /** The opening price, when the price has moved. */
  open?: number;
}

export function oddsMove(analysisOdds: number, opening?: number | null, current?: number | null): OddsMove {
  const open = Number(opening);
  const curr = Number(current);
  if (!Number.isFinite(open) || !Number.isFinite(curr) || open <= 1 || curr <= 1) return { odds: analysisOdds };
  const shift = 1 / curr - 1 / open; // positive = shortened = market support
  if (Math.abs(shift) < 0.02) return { odds: curr };
  const firming = shift > 0;
  const arrow = firming ? (shift >= 0.05 ? '▼▼' : '▼') : shift <= -0.05 ? '▲▲' : '▲';
  return { odds: curr, label: `${arrow} ${firming ? 'in' : 'out'} from $${open.toFixed(2)}`, dir: firming ? 'in' : 'out', open };
}

// ---------- scorecard and results ----------

export interface Scorecard {
  run: number;
  topWon: number;
  anyWon: number;
  /** Return on $1 on every top pick that ran, at SP, in %; null before any has. */
  roi: number | null;
}

/**
 * Has a pick settled for win ROI? It ran and either lost or won with its SP in. A winner's
 * position lands before its SP on race day; until the price arrives it is pending, out of
 * both stake and return, rather than a losing stake (which showed a winning day as -100%).
 */
export const settledWin = (pos: number, priceSp: unknown): boolean => pos > 0 && (pos !== 1 || Number(priceSp) > 0);

const topPick = (race: RaceResults) => race.results?.find((r) => r.pick_type === 'top' && r.pick_order === 1);

/**
 * The meeting so far, over the races run: top pick won, any tip won, and the
 * top pick's ROI at SP (the ResultsPage sums, except a scratched top pick is a
 * refund, not a losing stake).
 */
export function scorecard(results: RaceResults[] | null | undefined): Scorecard {
  const ran = (results ?? []).filter(isRun);
  let topWon = 0;
  let anyWon = 0;
  let stakes = 0;
  let returned = 0;
  for (const race of ran) {
    const first = topPick(race);
    if (first && positionOf(first) === 1) topWon++;
    if (race.results?.some((r) => r.pick_type && positionOf(r) === 1)) anyWon++;
    if (first && !first.scratched && settledWin(positionOf(first), first.price_sp)) {
      stakes++;
      if (positionOf(first) === 1) returned += Number(first.price_sp);
    }
  }
  return { run: ran.length, topWon, anyWon, roi: stakes ? ((returned - stakes) / stakes) * 100 : null };
}

/** The chips on a race's result: which of our bets landed. */
export function resultHits(race: RaceResults, segment?: Segment | null): string[] {
  const hits: string[] = [];
  if (positionOf(topPick(race)) === 1) hits.push('Top pick won');
  for (const r of race.results ?? []) {
    if (r.pick_type === 'top' && (r.pick_order ?? 0) > 1 && positionOf(r) === 1) hits.push(`Top ${r.pick_order} won`);
  }
  if (race.results?.some((r) => r.pick_type === 'value' && positionOf(r) === 1)) hits.push('Value won');
  if (segment?.quinella_hit) hits.push('Quinella');
  if (segment?.trifecta_hit) hits.push('Trifecta');
  if (segment?.first_four_hit) hits.push('First four');
  return hits;
}

export interface Finisher {
  pos: number;
  horse: string;
  sp: number | null;
  place: number | null;
  /** "Top 1", "Top 2", "Value" for our selections. */
  ours: string | null;
}

/** Finishing order, first to last, with our tips tagged. */
export const finishOrder = (race: RaceResults): Finisher[] =>
  (race.results ?? [])
    .filter((r) => !r.scratched && finished(positionOf(r)))
    .sort((a, b) => positionOf(a) - positionOf(b))
    .map((r) => ({
      pos: positionOf(r),
      horse: clean(r.horse_name),
      sp: r.price_sp ?? null,
      place: r.price_place ?? null,
      ours: r.pick_type === 'top' ? `Top ${r.pick_order ?? ''}`.trim() : r.pick_type === 'value' ? 'Value' : null,
    }));

/** Median winning SP at the meeting so far, and at its track over the 12 months before. */
export interface WinnerPrices {
  track_median: number | null;
  track_winners: number;
  day_median: number | null;
  day_winners: number;
}

// ponytail: a fixed sample floor; under it a track's "typical" winner is noise, so it isn't shown
export const MIN_TRACK_WINNERS = 20;

/**
 * How the day's median winner compares with the track's, as a multiple. Set from
 * 1,187 full meetings (Apr-Oct 2026): the day/track ratio's 10th-90th percentiles
 * are 0.70-1.68, so each band flags roughly the outer 5-16% of days.
 */
export const DAY_MOOD = { vlong: 2, long: 1.5, short: 0.75 } as const;
/** Winners needed on the day before it is called anything: one or two results say nothing yet. */
export const MIN_DAY_WINNERS = 3;

export type DayMood = 'vlong' | 'long' | 'normal' | 'short';

export const DAY_MOOD_LABEL: Record<DayMood, string> = {
  vlong: 'Roughies dominating',
  long: 'Roughies winning',
  normal: 'Normal day',
  short: 'Favourites winning',
};

/**
 * The scorecard's median-winner tile: the track's typical (median) winning SP,
 * today's so far, whether today's winners are paying longer (up) or shorter (down),
 * and what kind of day that makes it (null until there is enough to say).
 * Null when there is nothing worth showing.
 */
export function winnerPriceLine(wp: WinnerPrices | null | undefined) {
  if (!wp) return null;
  const typical = wp.track_winners >= MIN_TRACK_WINNERS ? wp.track_median : null;
  const today = wp.day_winners > 0 ? wp.day_median : null;
  if (typical == null && today == null) return null;
  const dir = typical != null && today != null && today !== typical ? (today > typical ? 'up' : 'down') : null;
  const ratio = typical != null && today != null && wp.day_winners >= MIN_DAY_WINNERS ? today / typical : null;
  const mood: DayMood | null =
    ratio == null ? null : ratio >= DAY_MOOD.vlong ? 'vlong' : ratio >= DAY_MOOD.long ? 'long' : ratio <= DAY_MOOD.short ? 'short' : 'normal';
  return { typical, today, todayWinners: wp.day_winners, trackWinners: wp.track_winners, dir, mood } as const;
}

// ---------- speed map ----------

export interface MapRow {
  tab: number;
  barrier: number | null;
  horse: string;
  bucket: string;
  settle: number;
  pick: BetClass | null;
  /** Bar width in %: longer = settles further forward; the shortest still fits a name. */
  width: number;
}

export const BUCKET_LABEL: Record<string, string> = { leaders: 'Lead', on_pace: 'On pace', midfield: 'Midfield', backmarkers: 'Back' };

/** Runners by barrier (outside at the top) and by settling order, plus those we cannot place. */
export function speedMapRows(map: SpeedMap, picks: Map<number, BetClass>) {
  const known: MapRow[] = [];
  const unknown: { tab: number; horse: string }[] = [];
  for (const r of map.runners) {
    if (r.settleAvg == null || r.bucket === 'unknown') {
      unknown.push({ tab: r.tabNumber, horse: clean(r.horseName) });
      continue;
    }
    known.push({
      tab: r.tabNumber,
      barrier: r.barrier,
      horse: clean(r.horseName),
      bucket: r.bucket,
      settle: r.settleAvg,
      pick: picks.get(r.tabNumber) ?? null,
      width: Math.round(52 + (1 - r.settleAvg) * 48),
    });
  }
  return {
    byBarrier: [...known].sort((a, b) => (b.barrier ?? 0) - (a.barrier ?? 0)),
    bySettle: [...known].sort((a, b) => a.settle - b.settle),
    unknown,
  };
}

// ---------- best bets (ported from racing-react utils/bestBetsUtils deriveBestBets) ----------

export interface DerivedBet {
  race: number;
  number: number;
  name: string;
  confidence: number;
  /** Our (assessed) price. */
  odds: number;
  marketOdds: number;
  adjustedAssessedProb: number;
  analysis: string;
  stake: string;
  edge: boolean;
  finishPosition?: number;
  sp?: number | null;
  resultStatus: 'won' | 'placed' | 'unplaced' | null;
}

const statusOf = (pos: number): DerivedBet['resultStatus'] => (!pos ? null : pos === 1 ? 'won' : pos <= 3 ? 'placed' : 'unplaced');

/**
 * The meeting's best bets: the two most likely winners (one per race, top
 * selections only), then the best remaining selection from another race, which
 * is the value bet if it is a 20%+ overlay on the market, else a third best bet.
 */
export function deriveBestBets(
  races: Race[] | null | undefined,
  scratchings: Scratching[] = [],
  results: RaceResults[] = [],
): { best: DerivedBet[]; value: DerivedBet | null } {
  type Cand = { race: number; sel: Selection; prob: number; isValue: boolean };
  const all: Cand[] = [];
  for (const race of races ?? []) {
    const add = (list: Selection[] | null | undefined, isValue: boolean) => {
      for (const sel of list ?? []) {
        if (isScratched(scratchings, race.race_number, sel.number)) continue;
        if (!sel.assessedOdds || !sel.confidence) continue;
        const prob = sel.adjustedAssessedProb ?? (100 / sel.assessedOdds) * (sel.confidence / 100);
        all.push({ race: race.race_number, sel, prob, isValue });
      }
    };
    add(race.selections?.top, false);
    add(race.selections?.value, true);
  }
  all.sort((a, b) => b.prob - a.prob);

  const build = (c: Cand, defaultStake: string): DerivedBet => {
    const res = findResult(
      results.find((r) => r.race_number === c.race),
      c.sel.number,
      c.sel.horse_name,
    );
    const pos = positionOf(res);
    return {
      race: c.race,
      number: c.sel.number,
      name: clean(c.sel.horse_name),
      confidence: c.sel.confidence,
      odds: c.sel.assessedOdds,
      marketOdds: c.sel.odds,
      adjustedAssessedProb: c.prob,
      analysis: c.sel.analysis?.summary ?? '',
      stake: c.sel.bet_type && c.sel.bet_stake ? c.sel.bet_stake : defaultStake,
      edge: !!c.sel.isConsensusPick,
      finishPosition: pos || undefined,
      sp: res?.price_sp ?? null,
      resultStatus: statusOf(pos),
    };
  };

  const best: DerivedBet[] = [];
  const seen = new Set<number>();
  for (const c of all) {
    if (seen.has(c.race) || c.isValue) continue;
    best.push(build(c, '3 units win.'));
    seen.add(c.race);
    if (best.length === 2) break;
  }
  let value: DerivedBet | null = null;
  const third = all.find((c) => !seen.has(c.race));
  if (third) {
    const implied = third.sel.odds ? 100 / third.sel.odds : 0;
    if (third.prob > implied * 1.2) value = build(third, '1 unit each way');
    else best.push(build(third, '3 units win.'));
  }
  return { best, value };
}

/** A day-wide best bet from dailyBestBets, as far as numbering needs it. */
export interface DayBet {
  trackCode?: string | null;
  raceNumber: number;
  tabNumber?: number | null;
}

/** 1-based place among the day's best bets, falling back to the order on this page. */
export const bestBetNumber = (day: DayBet[] | null | undefined, trackCode: string, race: number, number: number, fallback: number): number => {
  const i = (day ?? []).findIndex(
    (b) => b.trackCode?.toLowerCase() === trackCode.toLowerCase() && b.raceNumber === race && b.tabNumber === number,
  );
  return i >= 0 ? i + 1 : fallback;
};

// ---------- track record ----------

const STATE_SLUG: Record<string, string> = {
  victoria: 'vic',
  'new south wales': 'nsw',
  queensland: 'qld',
  'south australia': 'sa',
  'western australia': 'wa',
  tasmania: 'tas',
  'northern territory': 'nt',
  'australian capital territory': 'act',
};

/** A track's state as it appears in the tips URL ("VIC" or "Victoria" → "vic"). */
export const stateSlug = (state: string): string => STATE_SLUG[state.toLowerCase()] ?? state.toLowerCase().replace(/\s+/g, '-');

const dayMonth = (d: Date, year: boolean) =>
  d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) });

/**
 * A track-statistics meeting date as a local date. The server sends "31/08/2026"
 * (DD/MM/YYYY), which `new Date` would read as US month-first: 12/06 became 6 Dec
 * and any day past the 12th was dropped as invalid.
 */
export function meetingDay(s: string): Date {
  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
}

/** "Last 4 meetings, 14 Aug to 23 Sep 2026" / "All 37 meetings since Mar 2025". */
export function recordSpan(meetingDates: string[], period: 'recent' | 'all'): string {
  const dates = meetingDates
    .map(meetingDay)
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  const n = meetingDates.length;
  const word = n === 1 ? 'meeting' : 'meetings';
  if (!dates.length) return period === 'recent' ? 'Recent meetings' : 'All meetings';
  if (period === 'all') {
    return `All ${n} ${word} since ${dates[0].toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}`;
  }
  const first = dates[0];
  const last = dates[dates.length - 1];
  return n === 1 ? `Last meeting, ${dayMonth(last, true)}` : `Last ${n} ${word}, ${dayMonth(first, false)} to ${dayMonth(last, true)}`;
}

/**
 * The going the tips were formed on ("Good 4"), when the track has since changed grade
 * (Good to Soft); null while they agree or either is unknown. Tips are generated against
 * the forecast, so a different grade on the day is worth showing beside the actual.
 */
export function expectedGoing(
  actual: string | null | undefined,
  forecast: string | null | undefined,
  forecastNumber?: number | null,
): string | null {
  const grade = (s: string | null | undefined) => (s ?? '').trim().toLowerCase().split(' ')[0];
  const now = grade(actual);
  const then = grade(forecast);
  if (!now || !then || now === 'unknown' || then === 'unknown' || now === then) return null;
  return `${forecast!.trim()}${forecastNumber ? ` ${forecastNumber}` : ''}`;
}

// ---------- field view: every runner in a race, our picks marked ----------

/** One runner from raceField: the whole field, without our analysis. */
export interface FieldRunner {
  raceId: string;
  number: number;
  barrier?: number | null;
  horseName: string;
  jockey?: string | null;
  trainer?: string | null;
  weight?: number | null;
  oddsOpening?: number | null;
  oddsCurrent?: number | null;
  detailedInfo?: DetailedInfo | null;
}

export interface FieldPick {
  kind: 'top' | 'value';
  /** "Pick 1" / "Value", as on the cards. */
  label: string;
  bet: BetClass;
  sel: Selection;
}

export interface FieldRow {
  number: number;
  barrier: number | null;
  horse: string;
  jockey: string;
  trainer: string;
  weight: number | null;
  form: DetailedInfo;
  /** Latest market price; null until one has been collected. */
  odds: number | null;
  move?: string;
  dir?: 'in' | 'out';
  /** Opening price, set only when the price has moved. */
  opening?: number;
  /** Our selection, when the horse is one of our tips. */
  pick: FieldPick | null;
  /** Speed map: 0 leads, 1 settles last; null when the map cannot place it. */
  settle: number | null;
  bucket: string | null;
  scratched: boolean;
  result: ResultRow | null;
}

/** 'pick' (Pick 1, 2, 3, then Value) orders the Compact view; the Field view offers the rest. */
export type FieldSort = 'number' | 'odds' | 'barrier' | 'settle' | 'pick';

/**
 * A race's field as rows: every runner from raceField, with our tips, the speed map,
 * scratchings and results folded in. A pick missing from the field (the field not loaded
 * yet, or a runner the field query lacks) still gets its row from the tip itself.
 */
export function fieldRows(
  race: Race,
  runners: FieldRunner[],
  scratchings: Scratching[] | null | undefined,
  result: RaceResults | null | undefined,
): FieldRow[] {
  const picks = new Map<number, FieldPick>();
  for (const sel of race.selections?.top ?? []) {
    if (!picks.has(sel.number)) {
      picks.set(sel.number, {
        kind: 'top',
        label: sel.pick_order ? `Pick ${sel.pick_order}` : 'Pick',
        bet: betClass(sel.bet_type, sel.bet_stake),
        sel,
      });
    }
  }
  for (const sel of race.selections?.value ?? []) {
    if (!picks.has(sel.number))
      picks.set(sel.number, {
        kind: 'value',
        label: 'Value',
        bet: betClass(sel.bet_type, sel.bet_stake),
        sel,
      });
  }
  const mapped = new Map((race.selections?.speedMap?.runners ?? []).map((r) => [r.tabNumber, r]));

  const byNumber = new Map<number, FieldRunner>();
  for (const r of runners) byNumber.set(r.number, r);
  for (const [n, p] of picks) {
    if (byNumber.has(n)) continue;
    const s = p.sel;
    byNumber.set(n, {
      raceId: race.race_id,
      number: n,
      barrier: s.barrier,
      horseName: s.horse_name,
      jockey: s.jockey,
      trainer: s.trainer,
      weight: s.weight,
      oddsOpening: s.oddsOpening,
      oddsCurrent: s.oddsCurrent,
      detailedInfo: s.detailedInfo,
    });
  }

  return [...byNumber.values()].map((r) => {
    const pick = picks.get(r.number) ?? null;
    // a tip carries its analysis-time price, so it shows a price before the market is collected
    const move = oddsMove(pick?.sel.odds ?? 0, r.oddsOpening, r.oddsCurrent);
    const res = findResult(result, r.number, r.horseName);
    const m = mapped.get(r.number);
    const placed = m && m.settleAvg != null && m.bucket !== 'unknown';
    return {
      number: r.number,
      barrier: r.barrier || null,
      horse: clean(r.horseName),
      jockey: clean(r.jockey),
      trainer: clean(r.trainer),
      weight: r.weight || null,
      form: r.detailedInfo ?? pick?.sel.detailedInfo ?? {},
      odds: move.odds > 1 ? move.odds : null,
      move: move.label,
      dir: move.dir,
      opening: move.open,
      pick,
      settle: placed ? m.settleAvg : null,
      bucket: placed ? m.bucket : null,
      scratched: isScratched(scratchings, race.race_number, r.number) || !!res?.scratched,
      result: res,
    };
  });
}

/** Rows in the chosen order; scratched runners always go last, and unknowns sit after the known. */
export function sortField(rows: FieldRow[], by: FieldSort): FieldRow[] {
  const pickRank = (r: FieldRow): number | null => (!r.pick ? null : r.pick.kind === 'value' ? 100 : (r.pick.sel.pick_order ?? 50));
  const key = (r: FieldRow): number | null =>
    by === 'odds' ? r.odds : by === 'barrier' ? r.barrier : by === 'settle' ? r.settle : by === 'pick' ? pickRank(r) : r.number;
  return [...rows].sort((a, b) => {
    if (a.scratched !== b.scratched) return a.scratched ? 1 : -1;
    const ka = key(a);
    const kb = key(b);
    if (ka == null || kb == null) {
      if (ka != null) return -1;
      if (kb != null) return 1;
    } else if (ka !== kb) return ka - kb;
    return a.number - b.number;
  });
}
